/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import {steveDataStorage} from "../helpers/sdk-helpers";
import moment from "moment-timezone";
import PagingDeviceFeed from "../pagingfeed/device/paging-device-feed";
import DistinctValuesFeed from "../pagingfeed/lib/DistinctValuesFeed";
import PagingConfigAuditPolicyFeed from "../pagingfeed/audit/paging-config-audit-policy-feed";
import PagingOsImageServerFeed from "../pagingfeed/file-servers/paging-os-image-server-feed";
import PagingOsImageFeed from "../pagingfeed/files/paging-os-image-feed";
import PagingOsUpgradePlanFeed from "../pagingfeed/os-plans/paging-os-upgrade-plan-feed";
import PagingTriggerTypeFeed from "../pagingfeed/schedules/paging-trigger-type-feed";
/* Services */
import UserService from "./user-service";
import {parseDashboard, updateFavoriteDashboardsOrder} from "../helpers/dashboard-helpers";
import {error, success, warning} from "../helpers/notifications";
import {DATA_TYPE_NAMES} from "../components/dashboard/widgets/widget-constants";

const Steve = require("@gluenetworks/steve_core").getInstance();

const PlatformSdk = require("@gluenetworks/platform-sdk-remote");

const DashboardEntity = PlatformSdk.entities.ws.Dashboard;
const DashboardWidgetTypeEntity = PlatformSdk.entities.ws.DashboardWidgetType;
const DashboardPrefsEntity = PlatformSdk.entities.ws.DashboardPrefs;
const TriggerTypeEntity = PlatformSdk.entities.ws.TriggerType;
const DeviceEntity = PlatformSdk.entities.ws.Device;
const ConfigAuditPolicyEntity = PlatformSdk.entities.ws.ConfigAuditPolicy;
const OsImageServerEntity = PlatformSdk.entities.ws.OsImageServer;
const OsImageEntity = PlatformSdk.entities.ws.OsImage;
const OsUpgradePlanEntity = PlatformSdk.entities.ws.OsUpgradePlan;
const OsImagePathsEntity = PlatformSdk.entities.ws.OsImagePaths;
const FutureSchedulesEntity = PlatformSdk.entities.ws.FutureSched;
const TriggerEntity = PlatformSdk.entities.ws.Trigger;
const JobEntity = PlatformSdk.entities.ws.Job;
const ConstructTypeEntity = PlatformSdk.entities.ws.ConstructType;
export const CustomFieldEntity = PlatformSdk.entities.ws.CustomFields;

const SCHEDULE_QUERY = {triggerType: "SCHEDULE"};

const getWidgetTags = (tagsData, widgetTypeId) => {
    const tags = [];
    if (!tagsData) {
        return null;
    }
    for (const tagName in tagsData) {
        const tag = tagsData[tagName].tag;
        const types = tagsData[tagName].types;
        for (const type of types) {
            if (type.getId() === widgetTypeId) {
                tags.push({
                    title: tag.getName(),
                    id: tag.getId(),
                    bgColor: tag.getBgcolor(),
                });
            }
        }
    }

    return tags.reduce((acc, current) => {
        const x = acc.find((item) => item.id === current.id);
        if (!x) {
            return acc.concat([current]);
        }
        return acc;
    }, []);
};

const PRIVATE_DASHBOARD_VALUE = 1;
const PUBLIC_DASHBOARD_VALUE = 2;
const CUSTOM_IMAGE_VALUE = "CUSTOM";

const permissionOptionsList = [
    {
        id: PRIVATE_DASHBOARD_VALUE,
        title: "Private",
        description: "Only I can configure and run this dashboard",
    },
    {
        id: PUBLIC_DASHBOARD_VALUE,
        title: "Public",
        description: "All users can run this dashboard but they cannot configure it",
    },
];

const _getFilter = (filter) => {
    let _filter;
    try {
        if (filter && filter.length > 2) {
            _filter = {
                $and: [filter, SCHEDULE_QUERY],
            };
        } else {
            _filter = SCHEDULE_QUERY;
        }
    } catch (e) {
        _filter = filter;
    }
    return _filter;
};

class DashboardService {
    getUserId() {
        // eslint-disable-next-line no-underscore-dangle
        return window.DashboardComponent._userSession._userEntity.getId();
    }

    async getImageOptions() {
        try {
            const organizationId = UserService.getCurrentOrganizationId();
            const imageOptionsPrefs = await DashboardPrefsEntity.loadDashboardPrefs(organizationId);
            imageOptionsPrefs.setEnableImageUpload(true);
            imageOptionsPrefs.setEnableImages(true);
            return imageOptionsPrefs;
        } catch (e) {
            warning({message: e.errorMsg});
        }
    }

    async getDeviceInventoryItems(filter, fields) {
        try {
            const tenantId = UserService.getCurrentOrganizationId();

            const workTarget = {
                method: "query",
                category: "entity",
                entityType: "Device",
                query: filter && filter.mongoQuery ? filter.mongoQuery : {},
            };

            const workInput = {
                fields,
                emptyValueReplacement: "Blank",
            };

            const workResult = await DeviceEntity.runWork(
                tenantId,
                "inventorySummarization",
                workTarget,
                workInput,
                {}
            );

            const inventory = workResult.summary.inventory;

            inventory.forEach((el) => {
                el.children = inventory.filter((item) => {
                    return item.parent === el.id;
                });
            });

            return inventory;
        } catch (e) {
            warning({message: e.errorMsg});
        }
    }

    async getDevices(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();
        const additionalDataFields = [
            "connectionInformation",
            "customFieldValues",
            "detectedRawTypeBase",
            "nodeProps",
        ];

        return new PagingDeviceFeed({
            feedKey: "dashboard_device_feed",
            activeOrgId: organizationId,
            entityDef: DeviceEntity,
            columnConfig,
            filter: filter.hasOwnProperty("mongoQuery") ? filter.mongoQuery : filter,
            sort: userSort,
            additionalDataFields,
        }).whenFeedReady();
    }

    async getCustomFields() {
        const organizationId = UserService.getCurrentOrganizationId();

        return CustomFieldEntity.loadColumnDefinitions(organizationId);
    }

    async getAuditPolicies(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();

        return new PagingConfigAuditPolicyFeed({
            feedKey: "dashboard_config_audit_policy_feed",
            activeOrgId: organizationId,
            entityDef: ConfigAuditPolicyEntity,
            columnConfig,
            filter: filter.mongoQuery,
            sort: userSort,
        }).whenFeedReady();
    }

    async getFileServers(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();

        return new PagingOsImageServerFeed({
            feedKey: "dashboard_os_image_server_feed",
            activeOrgId: organizationId,
            entityDef: OsImageServerEntity,
            columnConfig,
            filter: filter.mongoQuery,
            sort: userSort,
        }).whenFeedReady();
    }

    async getFiles(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();

        return new PagingOsImageFeed({
            feedKey: "dashboard_os_image_feed",
            activeOrgId: organizationId,
            entityDef: OsImageEntity,
            columnConfig,
            filter: filter.mongoQuery,
            sort: userSort,
        }).whenFeedReady();
    }

    async getOsPlans(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();

        return new PagingOsUpgradePlanFeed({
            feedKey: "dashboard_os_upgrade_plan_feed",
            activeOrgId: organizationId,
            entityDef: OsUpgradePlanEntity,
            columnConfig,
            filter: filter.mongoQuery,
            sort: userSort,
        }).whenFeedReady();
    }

    async getSchedules(columnConfig, filter, userSort) {
        const organizationId = UserService.getCurrentOrganizationId();

        return new PagingTriggerTypeFeed({
            feedKey: "dashboard_trigger_type_feed",
            activeOrgId: organizationId,
            entityDef: TriggerTypeEntity,
            columnConfig,
            filter: _getFilter(filter.mongoQuery),
            sort: userSort,
        }).whenFeedReady();
    }

    getDeviceFieldLabels(property) {
        switch (property) {
            case "discoveryStatus":
                return DeviceEntity.getDiscoveryStatusLabels();
            case "latestHaveIChanged":
                return DeviceEntity.getLatestHaveIChangedLabels();
            case "osUpgradeStatus":
                return DeviceEntity.getOsUpgradeStatusLabels();
            case "osUpgradePlanAction":
                return DeviceEntity.getOsUpgradePlanActionLabels();
            case "accessStatus":
                return DeviceEntity.getAccessStatusLabels();
            case "auditStatus":
                return DeviceEntity.getAuditStatusLabels();
            case "rebootStatus":
                return DeviceEntity.getRebootStatusLabels();
            case "controlType":
                return DeviceEntity.getControlTypeLabels();
            default:
                return undefined;
        }
    }

    getAuditPolicyFieldLabels(property) {
        switch (property) {
            case "lastAuditStatus":
                return ConfigAuditPolicyEntity.getLastAuditStatusLabels();
            default:
                return undefined;
        }
    }

    getFileServersFieldLabels(property) {
        switch (property) {
            case "status":
                return OsImageServerEntity.getStatusLabels();
            default:
                return undefined;
        }
    }

    getFilesFieldLabels(property) {
        switch (property) {
            case "status":
                return OsImageEntity.getStatusLabels();
            default:
                return undefined;
        }
    }

    getOsPlansFieldLabels(property) {
        switch (property) {
            case "action":
                return OsUpgradePlanEntity.getActionLabels();
            case "status":
                return OsUpgradePlanEntity.getStatusLabels();
            default:
                return undefined;
        }
    }

    getSchedulesFieldLabels(property) {
        switch (property) {
            case "lastOccurrenceStatus":
                return TriggerTypeEntity.getLastOccurrenceStatusValues();
            default:
                return undefined;
        }
    }

    getDataChartLabels(fieldName, property) {
        switch (fieldName) {
            case DATA_TYPE_NAMES.AUDIT_POLICIES:
                return this.getAuditPolicyFieldLabels(property);
            case DATA_TYPE_NAMES.DEVICES:
                return this.getDeviceFieldLabels(property);
            case DATA_TYPE_NAMES.FILE_SERVERS:
                return this.getFileServersFieldLabels(property);
            case DATA_TYPE_NAMES.FILES:
                return this.getFilesFieldLabels(property);
            case DATA_TYPE_NAMES.OS_PLANS:
                return this.getOsPlansFieldLabels(property);
            case DATA_TYPE_NAMES.SCHEDULES:
                return this.getSchedulesFieldLabels(property);
            default:
                return undefined;
        }
    }

    async getDevicesDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return DeviceEntity.startDeviceDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(
            dataFieldName,
            dataQuery.hasOwnProperty("mongoQuery") ? dataQuery.mongoQuery : dataQuery
        );
    }

    async getAuditPoliciesDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return ConfigAuditPolicyEntity.startConfigAuditPolicyDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(dataFieldName, dataQuery.mongoQuery);
    }

    async getFileServersDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return OsImageServerEntity.startAscendOsImageServerDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(dataFieldName, dataQuery.mongoQuery);
    }

    async getFilesDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return OsImageEntity.startAscendOsImageDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(dataFieldName, dataQuery.mongoQuery);
    }

    async getOsPlansDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return OsUpgradePlanEntity.startOsUpgradePlanDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(dataFieldName, dataQuery.mongoQuery);
    }

    async getSchedulesDistinctValues(dataFieldName, dataQuery) {
        const organizationId = UserService.getCurrentOrganizationId();
        const distinctValue = new DistinctValuesFeed({
            startFeed: (fieldName, query) => {
                return TriggerTypeEntity.startTriggerTypeDistinctValuesFeed(
                    organizationId,
                    fieldName,
                    query,
                    "Blank"
                );
            },
        });
        return distinctValue.startFeed(dataFieldName, _getFilter(dataQuery.mongoQuery));
    }

    async launchJob(deviceName, command) {
        const organizationId = UserService.getCurrentOrganizationId();
        const device = await DeviceEntity.loadDeviceByName(organizationId, deviceName);
        const jobResult = await device.launchJob("show", {
            showCommands: [command],
        });

        return jobResult.jobId;
    }

    async getJobFeedForCommand(jobId) {
        const organizationId = UserService.getCurrentOrganizationId();
        const jobFeed = await JobEntity.startJobsFeed(organizationId, {
            id: jobId,
        });

        return jobFeed;
    }

    getPermissionSettingOptions() {
        return permissionOptionsList;
    }

    async getWidgetTypes(query) {
        try {
            const orgId = UserService.getCurrentOrganizationId();
            const tagsData = await DashboardWidgetTypeEntity.loadTagNameToTypesMap(orgId);
            const widgetTypes = [];
            let passedQuery = query;
            const queryArray = [];
            if (query !== null) {
                if (query.searchCriteria.length > 0) {
                    queryArray.push({
                        name: {$regex: `${query.searchCriteria}`, $options: "i"},
                    });
                }
                if (query.tagCriteria !== null) {
                    if (query.tagCriteria.length !== 0) {
                        queryArray.push({name: {$in: query.tagCriteria}});
                    }
                }
                if (queryArray.length > 0) {
                    passedQuery = {$or: queryArray};
                } else {
                    passedQuery = null;
                }
            }
            const widgetTypeData = await DashboardWidgetTypeEntity.loadDashboardWidgetTypes(
                orgId,
                passedQuery
            );
            if (widgetTypeData) {
                widgetTypeData.forEach((widgetType) => {
                    const widgetTypeId = widgetType.getId();
                    const widgetTypePristineProps = widgetType.getPristineProperties();
                    widgetTypes.push({
                        id: widgetTypeId,
                        title: widgetType.getName(),
                        description: widgetType.getDescription(),
                        tags: getWidgetTags(tagsData, widgetTypeId),
                        config: {
                            ...widgetType.getConfiguration(),
                            source: widgetType.source ? widgetType.source.elementTagName : null,
                        },
                        settings: {
                            DefaultW: widgetTypePristineProps.defaultWidth,
                            DefaultH: widgetTypePristineProps.defaultHeight,
                            MinW: widgetTypePristineProps.minWidth,
                            MinH: widgetTypePristineProps.minHeight,
                            MaxW: widgetTypePristineProps.maxWidth || Infinity,
                            MaxH: widgetTypePristineProps.maxHeight || Infinity,
                        },
                        images: widgetType.getImages(),
                        maxRunningWidgets: widgetType.getMaxRunningWidgets(),
                        version: widgetTypePristineProps.version,
                    });
                });
            }
            return widgetTypes;
        } catch (e) {
            warning({message: e.errorMsg});
            return false;
        }
    }

    async fetchTags() {
        const organizationId = UserService.getCurrentOrganizationId();
        const tagsData = await DashboardWidgetTypeEntity.loadTagNameToTypesMap(organizationId);
        const formatedTagsData = {};
        Object.keys(tagsData).forEach((key) => {
            formatedTagsData[key] = {
                title: key,
                id: tagsData[key].tag.id,
                label: key,
                value: key,
                bgColor: tagsData[key].tag.bgcolor,
                widgets: tagsData[key].types.map((entity) => entity.getName()),
            };
        });
        return formatedTagsData;
    }

    async getDashboard(id, parseData = false, widgetTypes, widgetTags) {
        try {
            const organizationId = UserService.getCurrentOrganizationId();
            // We load dashboards because of filtering on organization level
            const dashboardList = await DashboardEntity.loadAscendDashboards(organizationId, {id});
            if (!dashboardList.length) {
                return null;
            }
            const dashboard = dashboardList[0];
            const controlEntity = Steve.controlAppNav._controluserInstance;
            const isFavoriteDashboard = await dashboard.isControlUserFavorite(controlEntity);
            if (dashboard) {
                return !parseData
                    ? dashboard
                    : parseDashboard(
                          {
                              dashboard,
                              isFavorite: isFavoriteDashboard,
                          },
                          widgetTypes,
                          widgetTags
                      );
            }
        } catch (e) {
            error({message: e.errorMsg});
        }
        return null;
    }

    async addToFavorites(dashboardId) {
        try {
            const userId = this.getUserId();
            const organizationId = UserService.getCurrentOrganizationId();
            const dashboard = await DashboardEntity.loadAscendDashboard(
                organizationId,
                dashboardId
            );
            await dashboard.addToFavorites(userId);
            updateFavoriteDashboardsOrder(dashboard, "ADD");
        } catch (e) {
            error({message: e.errorMsg});
        }
    }

    async removeFromFavorite(dashboardId) {
        try {
            const userId = this.getUserId();
            const organizationId = UserService.getCurrentOrganizationId();
            const dashboard = await DashboardEntity.loadAscendDashboard(
                organizationId,
                dashboardId
            );
            dashboard.removeFromFavorites(userId);
            updateFavoriteDashboardsOrder(dashboard, "REMOVE");
        } catch (e) {
            error({message: e.errorMsg});
        }
    }

    async getAll(query) {
        let dashboardsEntities = [];
        let dashboards = [];
        try {
            let passedQuery = query;
            const queryArray = [];
            if (query !== null) {
                if (query.searchCriteria.length > 0) {
                    queryArray.push({
                        name: {$regex: `${query.searchCriteria}`, $options: "i"},
                    });
                }
                if (query.isPrivate !== null) {
                    queryArray.push({private: query.isPrivate});
                }
                if (query.isShared !== null) {
                    queryArray.push({shared: query.isShared});
                }
                if (query.tagIds !== null) {
                    if (query.tagIds.length !== 0) {
                        queryArray.push({widgetTagIds: {$all: query.tagIds}});
                    }
                }
                if (queryArray.length > 0) {
                    passedQuery = {$and: queryArray};
                } else {
                    passedQuery = null;
                }
            }
            const organizationId = UserService.getCurrentOrganizationId();
            dashboardsEntities = await DashboardEntity.loadAscendDashboards(
                organizationId,
                passedQuery
            );
            const controlEntity = Steve.controlAppNav._controluserInstance;
            const isFavoriteDashboard = await Promise.all(
                dashboardsEntities.map((dashboard) =>
                    dashboard.isControlUserFavorite(controlEntity)
                )
            );
            dashboards = dashboardsEntities.map((dashboard, index) => {
                return {
                    dashboard,
                    isFavorite: isFavoriteDashboard[index],
                };
            });
        } catch (e) {
            error({message: e.errorMsg});
        }
        return dashboards;
    }

    setDashboardValues = (dashboard, dashboardData, userId) => {
        if (!dashboard) {
            return;
        }
        dashboard
            .setName(dashboardData.dashboardName)
            .setOwnerId(userId)
            .setDescription(dashboardData.dashboardDescription)
            .setPrivate(
                Number(dashboardData.selectedPermissionSettingsId) === PRIVATE_DASHBOARD_VALUE
            )
            .setShared(dashboardData.isShared)
            .setImageOption(dashboardData.selectedImageOptionId);

        if (dashboardData.selectedImageOptionId === CUSTOM_IMAGE_VALUE) {
            dashboard.setImage(dashboardData.image);
        }
    };

    mapToLgWidgets = (smallLayouts, lgLayout, widgets, widgetTypes) => {
        const widgetsNotInLgLayout = Object.values(smallLayouts)
            .flat()
            .filter((widget) => lgLayout.every((lgWidget) => lgWidget.i !== widget.i));

        const widgetsWithDefaultWidth = widgetsNotInLgLayout.map((widget) => {
            const widgetType = widgetTypes.find(
                (widgetType) => widgetType.id === widgets[widget.i].typeId
            );
            return {...widget, w: widgetType.settings.DefaultW};
        });

        return [...widgetsWithDefaultWidth, ...lgLayout];
    };

    async publish(dashboardData, id, isCloned, widgetTypes) {
        const userId = this.getUserId();
        const organizationId = UserService.getCurrentOrganizationId();
        const runningDashboardId = steveDataStorage("dashboards", "latest", false).get("running");
        const {widgets, layouts} = dashboardData;
        const dashboardName = dashboardData.dashboardName ? dashboardData.dashboardName : "";
        try {
            const dashboard = id
                ? await DashboardEntity.loadDashboard(organizationId, id)
                : new DashboardEntity(organizationId);
            this.setDashboardValues(dashboard, dashboardData, userId);

            const mappedWidgetsData = {};

            let allLayouts = [...layouts.lg];

            const {lg, ...smallLayouts} = layouts;
            if (!isCloned && Object.keys(smallLayouts).length) {
                allLayouts = this.mapToLgWidgets(smallLayouts, lg, widgets, widgetTypes);
            }

            Object.entries(widgets).forEach(([widgetId, widgetData]) => {
                const layout = Object.values(allLayouts).find((lay) => lay.i === widgetId);
                const newLayout = {
                    w: layout.w,
                    h: layout.h,
                    x: layout.x,
                    y: layout.y,
                };
                mappedWidgetsData[widgetId] = {
                    config: widgetData.config,
                    title: widgetData.title,
                    id: widgetId,
                    typeId: widgetData.typeId,
                    layouts: {lg: newLayout},
                    version: widgetData.version,
                };
            });

            dashboard.setWidgets(mappedWidgetsData);

            if (id) {
                if (dashboard.isDirty() && dashboard.pendingPatches) {
                    await dashboard.update();
                }
            } else {
                await dashboard.create();
            }

            const controlEntity = Steve.controlAppNav._controluserInstance;
            const isFavorite = await dashboard
                .isControlUserFavorite(controlEntity)
                .then((isFavorite) => isFavorite);

            if (isFavorite && !dashboardData.isFavorite) {
                await dashboard.removeFromFavorites(userId);
                if (runningDashboardId === dashboard.getId()) {
                    steveDataStorage("dashboards", "latest", false).set(null, "running");
                }
            } else if (!isFavorite && dashboardData.isFavorite) {
                await dashboard.addToFavorites(userId);
            }

            if (id) {
                success({
                    name: dashboardName,
                    message: " dashboard successfully updated. You can find it in your library.",
                });
            } else {
                success({
                    name: dashboardName,
                    message: ` dashboard successfully ${
                        isCloned ? "cloned" : "published"
                    }. You can find it in your library.`,
                });
            }
            return true;
        } catch (e) {
            if (e.code === 11000) {
                error({
                    message: `Error creating dashboard: a dashboard with the name ${dashboardName} already exists in this organization.`,
                });
            } else {
                error({message: e.errorMsg});
            }
            return false;
        }
    }

    async importDashboard(dashboardData, isWarning) {
        const userId = this.getUserId();
        const organizationId = UserService.getCurrentOrganizationId();

        const {widgets, dashboardName} = dashboardData;
        try {
            const dashboard = new DashboardEntity(organizationId);
            this.setDashboardValues(dashboard, dashboardData, userId);

            dashboard.setWidgets(widgets);
            await dashboard.create();
            if (isWarning) {
                warning({
                    name: "Warning: ",
                    message: `The ${dashboardName} dashboard was imported.
                                               Some widgets were not imported because a newer version already exists.`,
                });
            } else {
                success({
                    name: "Success! ",
                    message: `The ${dashboardName} dashboard you imported is now in your Dashboard Library.`,
                });
            }

            return true;
        } catch (e) {
            if (e.code === 11000) {
                error({
                    name: "Error! ",
                    message: `Something went wrong. We’re unable to import the ${dashboardName} dashboard.`,
                });
            } else {
                error(e.errorMsg);
            }
            return false;
        }
    }

    update() {
        success({message: "Dashboard successfully updated"});
        return true;
    }

    async delete(dashboard) {
        try {
            if (dashboard) {
                const runningDashboardId = steveDataStorage("dashboards", "latest", false).get(
                    "running"
                );
                if (dashboard.modalFormData.isFavorite) {
                    const userId = this.getUserId();
                    const organizationId = UserService.getCurrentOrganizationId();
                    const selectedDashboard = await DashboardEntity.loadDashboardByName(
                        organizationId,
                        dashboard.title
                    );
                    await selectedDashboard.removeFromFavorites(userId);
                    updateFavoriteDashboardsOrder(selectedDashboard, "REMOVE");
                }
                await dashboard.delete();
                if (runningDashboardId === dashboard.id) {
                    steveDataStorage("dashboards", "latest", false).set(null, "running");
                }
            }
        } catch (e) {
            error({message: "Something went wrong, please try again later."});
            return false;
        }
        return true;
    }

    async getSchedulesforCalendar(query) {
        try {
            const organizationId = UserService.getCurrentOrganizationId();
            const triggerFeed = await TriggerTypeEntity.startTriggerTypesFeed(
                organizationId,
                _getFilter(query),
                {},
                false,
                false
            );
            return triggerFeed.whenInitialized();
        } catch (err) {
            error({message: "Something went wrong, please try again later."});
            return false;
        }
    }

    async getFutureSchedules(triggerTypes) {
        try {
            let futureSchedules = {};
            const query = {triggerTypeId: {$in: triggerTypes}};
            /* Commenting the time interval query until it's wired up to the calendar component
            const dateString = new Date().setUTCMonth(new Date().getMonth() + 1);
            const query = { $and: [
                { triggerTypeId: { $in: triggerTypes } },
                { expectedExecutionTime: { $lte: dateString } }
            ]
            };
            */
            const organizationId = UserService.getCurrentOrganizationId();
            futureSchedules = await FutureSchedulesEntity.loadFutureScheds(
                organizationId,
                query,
                {},
                false,
                false
            );
            return futureSchedules;
        } catch (err) {
            error({message: "Something went wrong, please try again later."});
            return false;
        }
    }

    async getPastScheduleTriggers(triggerTypes) {
        try {
            let pastSchedules = {};
            const query = {triggerTypeId: {$in: triggerTypes}};
            /* Commenting the time interval query until it's wired up to the calendar component
            const dateString = new Date().setUTCMonth(new Date().getMonth() - 1);
            const query = { $and: [
                { triggerTypeId: { $in: triggerTypes } },
                { actualExecutionTime: { $gte: dateString } }
            ]
            };
            */
            const organizationId = UserService.getCurrentOrganizationId();
            pastSchedules = await TriggerEntity.loadTriggers(
                organizationId,
                query,
                {},
                false,
                false
            );
            return pastSchedules;
        } catch (err) {
            error({message: "Something went wrong, please try again later."});
            return false;
        }
    }

    async loadOsMapForGridWidget() {
        return DeviceEntity.loadOsMap(UserService.getCurrentOrganizationId());
    }

    async loadOsPathsForGridWidget() {
        return OsImagePathsEntity.loadPaths(UserService.getCurrentOrganizationId());
    }

    getLicenseStateForLicensingWidget() {
        return Steve.licenseRestriction;
    }

    adjustDashboardWidgetsForImport(dashboard, currentWidgetTypes) {
        const {
            defaultImage: image,
            description: dashboardDescription,
            name: dashboardName,
            shared: isShared,
            imageOption: selectedImageOptionId,
            widgets,
        } = dashboard;

        let importedDashboard = {
            image,
            dashboardDescription,
            dashboardName,
            isShared,
            selectedImageOptionId,
            widgets,
            selectedPermissionSettingsId: PRIVATE_DASHBOARD_VALUE,
        };

        let importedWidgetTypes = Object.values(importedDashboard.widgets);

        importedWidgetTypes = importedWidgetTypes.filter((importWidget) => {
            // Note that widgets in the JSON file whose widgetTypeId don't exist in the destination instance are dropped.
            const widgetType = currentWidgetTypes.find(
                (widget) => widget.id === importWidget.typeId
            );
            if (!widgetType) {
                return false;
            }

            const {version: currVersion = "1.0.0"} = widgetType;
            const {version: importedVersion = "1.0.0"} = importWidget;

            // widgets whose versions do no match except for minor version differences (semver's last digit)
            const currFirstDigit = currVersion.split(".")[0];
            const importedFirstDigit = importedVersion.split(".")[0];
            return currFirstDigit === importedFirstDigit;
        });

        const filteredWidgets = importedWidgetTypes.reduce(
            (acc, item) => ({...acc, [item.id]: item}),
            {}
        );

        importedDashboard = {
            ...importedDashboard,
            widgets: filteredWidgets,
        };

        return importedDashboard;
    }

    adjustDashboardsImportNames(newJsonDashboards, allDashboards) {
        const newDashboards = newJsonDashboards.map((newDashboard) => {
            const indexLast_ = newDashboard.name.lastIndexOf("_");
            const stringAfterLast_ = newDashboard.name.slice(indexLast_ + 1);

            const newNameWithoutCount = isNaN(stringAfterLast_)
                ? newDashboard.name
                : newDashboard.name.slice(0, indexLast_);

            //find max number of copy
            const nameCount = allDashboards.reduce((maxIndex, dashboard) => {
                if (newNameWithoutCount === dashboard.title && maxIndex === -1) {
                    return 0;
                } else if (dashboard.title.startsWith(`${newNameWithoutCount}_`)) {
                    const stringAfterLast_ = dashboard.title.slice(
                        dashboard.title.lastIndexOf("_") + 1
                    );
                    const count = isNaN(stringAfterLast_) ? -1 : Number(stringAfterLast_);
                    return count > maxIndex ? count : maxIndex;
                }

                return maxIndex;
            }, -1);

            return {
                ...newDashboard,
                name:
                    nameCount === -1
                        ? newDashboard.name
                        : `${newNameWithoutCount}_${nameCount + 1}`,
            };
        });

        // check if there are duplicate names in the new array
        for (let i = newDashboards.length - 1; i > -1; i--) {
            let dashboard = newDashboards[i];

            const stringAfterLast_ = dashboard.name.slice(dashboard.name.lastIndexOf("_") + 1);
            let count = isNaN(stringAfterLast_) ? 0 : Number(stringAfterLast_);

            let counter = 0;

            for (let j = 0; j < i; j++) {
                if (i === 0) {
                    continue;
                }

                if (newDashboards[j].name.startsWith(dashboard.name)) {
                    counter++;
                }
            }
            if (counter > 0) {
                dashboard.name = dashboard.name.replace(`${count}`, `${count + counter}`);
            }
        }

        return newDashboards;
    }

    generateExportFileName(onlyOneDashboardName) {
        // DASH_Export_<YYYYMMDD>_<HHMM>_<TMZ>.json
        const dt = new Date();
        const dateTimeString = moment(dt).format("YYYYMMDD_HHmm");

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timeZoneString = moment().tz(timeZone).zoneAbbr();

        return `DASH_Export${onlyOneDashboardName}_${dateTimeString}_${timeZoneString}`;
    }

    async getTotalCountActivityStatistics(startTime, endTime, countTypes) {
        const workTarget = {method: "query", query: {}};
        const workInput = {
            startTime,
            endTime,
            countTypes,
        };
        try {
            const tenantId = UserService.getCurrentOrganizationId();
            const workResult = await DeviceEntity.runActivityCountWork(
                tenantId,
                workTarget,
                workInput
            );
            if (workResult.errorMsg) {
                error({message: workResult.errorMsg});
                return null;
            }
            return workResult.summary.counts;
        } catch (err) {
            error({message: "Something went wrong, please try again later."});
            return null;
        }
    }

    // KEPT FOR USE IN FUTURE WHEN NEEDED
    // async getTotalCountChangeStatistics(startTime, endTime, countTypes) {
    //     const workTarget = { method: 'query' , query: {} };
    //     const workInput = {
    //         startTime,
    //         endTime,
    //         countTypes
    //     };
    //     try {
    //         const tenantId = UserService.getCurrentOrganizationId();
    //         const workResult = await DeviceEntity.runChangeCountWork(tenantId, workTarget, workInput);
    //         if (workResult.errorMsg) {
    //             error({ message: workResult.errorMsg });
    //             return null;
    //         }
    //         return workResult.summary.counts;
    //     } catch (err) {
    //         error({ message: 'Something went wrong, please try again later.' });
    //         return null;
    //     }
    // }

    async loadDevices(input) {
        const organizationId = UserService.getCurrentOrganizationId();
        const filter = {
            $and: [
                {
                    detectedVendor: {
                        $eq: "Cisco",
                    },
                },
                {
                    $or: [
                        {
                            name: {
                                $regex: `^${input}`,
                                $options: "i",
                            },
                        },
                        {
                            "connectionInformation.ip": {
                                $regex: `^${input}`,
                                $options: "i",
                            },
                        },
                    ],
                },
            ],
        };

        return await DeviceEntity.loadDevices(organizationId, filter, {}, {}, {skip: 0, limit: 5});
    }

    async getRssFeed(urls) {
        let ConnectionHandlerFactory = PlatformSdk.connectionFactories.ConnectionHandlerFactory;
        let connection = ConnectionHandlerFactory.getInstance();

        let feeds;
        try {
            feeds = await new Promise((resolve, reject) => {
                connection.request(
                    {
                        service: "SystemService",
                        method: "getRssFeed",
                        payload: {urls},
                    },
                    {Success: resolve, Failure: reject}
                );
            });
        } catch (e) {
            return {[urls]: undefined};
        }

        return feeds;
    }

    async _getNodeProps() {
        const organizationId = UserService.getCurrentOrganizationId();
        return ConstructTypeEntity.getDeviceNodePropDefs(organizationId);
    }
}

export default new DashboardService();
